import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useAuth } from "../contexts/AuthContext";
import { canEditContent } from "../utils/permissions";
import { useLang } from "../contexts/LanguageContext";
import {
  PaperPlaneRight, Plus, MagnifyingGlass, Paperclip, Image as ImageIcon,
  SpinnerGap, Check, Checks, UserCircle, UsersThree, X, DownloadSimple,
  ChatCircleDots, ArrowLeft, Robot, CheckCircle, Trash, Sparkle, PencilSimple,
  Archive, ArrowCounterClockwise, SpeakerHigh, SpeakerSlash, ChartBar
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

import { API_BASE as API } from "../lib/apiBase";
import { useSmartScrollAndNewMessages } from "../hooks/useSmartScrollAndNewMessages";
import { useOneTimeHint } from "../hooks/useOneTimeHint";
import { isSoundEnabled, setSoundEnabled, playDing, syncSoundFromServer } from "../lib/messagingSound";
import PollCard from "../components/messaging/PollCard";
import { texteMessage } from "../lib/texteMessage";
import PollComposer from "../components/messaging/PollComposer";
import PinnedPoll from "../components/messaging/PinnedPoll";
import { usePollPermission } from "../hooks/usePollPermission";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  const time = d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 0) return time;
  if (diffDays === 1) return `Hier ${time}`;
  return d.toLocaleDateString("fr-CA", { day: "numeric", month: "short" }) + ` ${time}`;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function AgoraPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  // Tâche 1424 — Sondages aussi dans les conversations privées : Eric a
  // demandé « dans un canal COMME dans une conversation ».
  const { canCreatePoll } = usePollPermission();
  const [showPollComposer, setShowPollComposer] = useState(false);
  // Vincent #120 (Eric 2026-05-13) — DM indexation gating.
  // Seuls les rôles « expérimentés » (Fondateur, Admin, Concepteur, Directeur/agent
  // expérimenté = access_level "avance") voient les contrôles d'indexation chatbot
  // sur les messages individuels. Conseillers (employee_trained / "intermediaire")
  // et Stagiaires (employee_new / "debutant") n'y ont pas accès.
  // canIndexDm = canEditContent (mêmes critères : éditeurs de contenu peuvent
  // indexer des DMs au chatbot). iter116 — migration vers helper canonique.
  const canIndexDm = canEditContent(user);
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  // Eric 2026-07-30 (B596 enhancement) — État miroir du localStorage pour
  // le toggle son. Init une fois au mount depuis `isSoundEnabled()`.
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());

  // Eric 2026-07-30 (B596.d) — Hydrate depuis les préférences serveur
  // (`user.preferences.messaging_sound_enabled`). Permet de retrouver le
  // même réglage cross-device (desktop ↔ mobile). Ne fait rien si serveur
  // et local sont déjà en accord.
  useEffect(() => {
    const changed = syncSoundFromServer(user?.preferences);
    if (changed) setSoundOn(isSoundEnabled());
  }, [user?.preferences]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [convoSearch, setConvoSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  // Vincent B591 (Eric 2026-07-28 SPEC STRICK) — Édition des membres d'un
  // groupe (créateur du groupe OU rôle admin/founder).
  const [showEditMembers, setShowEditMembers] = useState(false);
  const [editMembersSelected, setEditMembersSelected] = useState(new Set());
  const [editMembersBusy, setEditMembersBusy] = useState(false);
  // Vincent B597 (Eric 2026-07-28) — Toggle archives (Gmail-style, par user)
  const [showArchived, setShowArchived] = useState(false);
  // Vincent B598.c (Eric 2026-07-29) — Hint 1× par session pour signaler
  // que l'archivage est possible au hover sur chaque conversation.
  const nonEmptyList = conversations.length > 0;
  const { show: showArchiveHint, dismiss: dismissArchiveHint } = useOneTimeHint(
    "kn-agora-archive-hint-seen", 10000, nonEmptyList
  );
  const [editMembersSearch, setEditMembersSearch] = useState("");
  const canEditMembers = activeConvo && activeConvo.is_group && (
    activeConvo.created_by === user?.id ||
    user?.is_founder ||
    user?.role === "admin" ||
    user?.role === "founder"
  );
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  // Vincent B596.d (Eric 2026-07-28) — Logique scroll intelligent + bouton
  // flottant « ↓ Nouveaux messages » + pulse — factorisée dans un hook partagé
  // avec ChannelsPanel pour éviter la duplication.
  const {
    messagesContainerRef,
    messagesEndRef,
    onMessagesScroll,
    newMsgCount,
    pulse,
    scrollToLatest,
    markJustSent,
    forceScrollOnNextRender,
  } = useSmartScrollAndNewMessages(messages, activeConvo?.id);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/agora/conversations`,
        { params: { include_archived: showArchived }, withCredentials: true }
      );
      setConversations(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [showArchived]);

  const loadMessages = useCallback(async (convoId, silent) => {
    try {
      const { data } = await axios.get(`${API}/api/agora/conversations/${convoId}/messages`, { withCredentials: true });
      if (!silent || data.messages.length !== messages.length) {
        setMessages(data.messages);
      }
      if (!silent) loadConversations();
    } catch (e) { console.error(e); }
  }, [messages.length, loadConversations]);

  useEffect(() => {
    loadConversations();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [loadConversations]);

  // Vincent B716 (Eric 2026-02-11) — Ouverture automatique d'une conversation
  // via param URL `?convo=<id>` (ex: depuis SpeakersPanel B716 quand on
  // clique sur un locuteur avec user plateforme associé). Fonctionne aussi
  // avec `?dm=<user_id>` : on crée (ou récupère) la conv 1-to-1 et on ouvre.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const convoParam = sp.get("convo");
    const dmParam = sp.get("dm");
    if (!convoParam && !dmParam) return;
    // Vincent B716 : le flow DM doit fonctionner même quand l'utilisateur
    // n'a AUCUNE conversation (première interaction). Pour `?convo=<id>`,
    // on attend d'avoir la liste chargée. Pour `?dm=<uid>`, on crée
    // immédiatement.
    if (convoParam && (!conversations || conversations.length === 0)) return;
    (async () => {
      let target = null;
      if (convoParam) {
        target = conversations.find((c) => c.id === convoParam);
      } else if (dmParam) {
        try {
          const { data } = await axios.post(
            `${API}/api/agora/conversations`,
            { member_ids: [dmParam], is_group: false },
            { withCredentials: true }
          );
          target = data;
          // Recharge la liste au cas où la conv vient d'être créée.
          loadConversations();
        } catch (err) { console.error("[B716] open DM failed", err); }
      }
      if (target) {
        setActiveConvo(target);
        setMobileShowChat(true);
        forceScrollOnNextRender();
        loadMessages(target.id);
      }
      // Nettoie l'URL pour éviter la ré-ouverture au reload.
      const newSp = new URLSearchParams(window.location.search);
      newSp.delete("convo");
      newSp.delete("dm");
      const q = newSp.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  // Vincent B597 — reload lorsqu'on switch archives ↔ actives
  // Note : loadConversations dépend déjà de showArchived (useCallback), donc
  // l'effet ci-dessus se re-tire automatiquement quand showArchived change.

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (activeConvo) {
      pollingRef.current = setInterval(() => { loadMessages(activeConvo.id, true); }, 3000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [activeConvo, loadMessages]);

  // Vincent B597 (Eric 2026-07-28 SPEC STRICK) — Archive/désarchive une conv
  // pour le user courant uniquement (Gmail-style).
  const toggleArchive = async (convoId, archived, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.patch(
        `${API}/api/agora/conversations/${convoId}/archive`,
        { archived },
        { withCredentials: true }
      );
      // Si on archive la conv active → on la ferme
      if (archived && activeConvo?.id === convoId) setActiveConvo(null);
      loadConversations();
    } catch (err) { console.error(err); }
  };

  const openConvo = (convo) => {
    setActiveConvo(convo);
    setMobileShowChat(true);
    // Vincent B596 — L'ouverture d'une conversation doit toujours démarrer en bas.
    forceScrollOnNextRender();
    loadMessages(convo.id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConvo) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/agora/conversations/${activeConvo.id}/messages`, { text: newMsg }, { withCredentials: true });
      setNewMsg("");
      // Vincent B596 — Force le scroll en bas SEULEMENT quand user envoie
      markJustSent();
      loadMessages(activeConvo.id, true);
      loadConversations();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  // Vincent #115 (2026-05-12) — Toggle per-message indexation for a DM.
  // include = true → force include, false → exclude, null → default (off for DMs).
  const toggleDmIndex = async (msg, include) => {
    try {
      const { data } = await axios.patch(
        `${API}/api/agora/messages/${msg.id}/index`,
        { include },
        { withCredentials: true }
      );
      setMessages((cur) => cur.map((m) => (m.id === msg.id ? { ...m, index_override: data.index_override, indexed_by_chatbot: data.indexed_by_chatbot } : m)));
    } catch (e) {
      console.error("toggleDmIndex:", e);
    }
  };

  // Vincent #135 (Eric 2026-05-14) → AgoraPage (Eric 2026-05-16) — Soft-delete
  // d'un message DM individuel. L'auteur (ou un admin/concepteur) peut effacer
  // son message ; il disparait pour tous les participants ET est retire de
  // l'indexation RAG (Loi 25 : trace audit conservee cote DB avec deleted_at).
  const deleteDmMessage = async (msg) => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Supprimer ce message ? Il sera masqué pour tous les participants et retiré de l'indexation du chatbot."
        : "Delete this message? It will be hidden for all participants and removed from chatbot indexing.",
      { danger: true, confirmLabel: lang === "fr" ? "Supprimer" : "Delete" },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/agora/messages/${msg.id}`, { withCredentials: true });
      setMessages((cur) => cur.filter((m) => m.id !== msg.id));
      toast.success(lang === "fr" ? "Message supprimé" : "Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur lors de la suppression" : "Delete failed"));
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !activeConvo) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/api/agora/conversations/${activeConvo.id}/upload?text=${encodeURIComponent(file.name)}`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      loadMessages(activeConvo.id, true);
      loadConversations();
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  const startNewConvo = async () => {
    if (selectedMembers.length === 0) return;
    try {
      const isGroup = selectedMembers.length > 1;
      const { data } = await axios.post(`${API}/api/agora/conversations`, {
        member_ids: selectedMembers.map(m => m.id),
        is_group: isGroup,
        // Bug 543 v2 (Eric 2026-07-24) — Trie les prénoms par ordre alpha
        // avant de générer le nom du groupe par défaut. Le user ne veut plus
        // voir "Raphaël, Vincent, Jean" mais "Jean, Raphaël, Vincent".
        name: isGroup
          ? (groupName || [...selectedMembers]
              .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }))
              .map(m => m.name.split(" ")[0])
              .join(", "))
          : ""
      }, { withCredentials: true });
      setShowNewConvo(false);
      setSelectedMembers([]);
      setGroupName("");
      setMemberSearch("");
      loadConversations();
      openConvo(data);
    } catch (e) { console.error(e); }
  };

  const openEditMembers = async () => {
    if (!activeConvo) return;
    // Charge la liste des membres de l'org si pas déjà chargée
    if (orgMembers.length === 0) {
      try {
        const { data } = await axios.get(`${API}/api/agora/org-members`, { withCredentials: true });
        setOrgMembers(data);
      } catch (e) { console.error(e); }
    }
    setEditMembersSelected(new Set(activeConvo.members || []));
    setEditMembersSearch("");
    setShowEditMembers(true);
  };

  const saveMembers = async () => {
    if (!activeConvo || editMembersBusy) return;
    const current = new Set(activeConvo.members || []);
    const target = new Set(editMembersSelected);
    const add = [...target].filter((id) => !current.has(id));
    const remove = [...current].filter((id) => !target.has(id));
    if (add.length === 0 && remove.length === 0) {
      setShowEditMembers(false);
      return;
    }
    setEditMembersBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/agora/conversations/${activeConvo.id}/members`,
        { add, remove },
        { withCredentials: true }
      );
      // Merge updated members back into local state
      setActiveConvo({ ...activeConvo, members: data.members, member_names: data.member_names });
      setConversations((prev) => prev.map((c) => c.id === activeConvo.id
        ? { ...c, members: data.members, member_names: data.member_names }
        : c
      ));
      toast.success(`Membres mis à jour (+${add.length} / −${remove.length})`);
      setShowEditMembers(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible d'éditer les membres";
      toast.error(msg);
    } finally {
      setEditMembersBusy(false);
    }
  };



  const loadOrgMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/agora/org-members`, { withCredentials: true });
      setOrgMembers(data);
    } catch (e) { console.error(e); }
  };

  const getConvoName = (convo) => {
    if (convo.is_group) return convo.name || "Groupe";
    if (convo.other_user) return convo.other_user.name;
    const names = Object.entries(convo.member_names || {}).filter(([id]) => id !== user?.id).map(([, n]) => n);
    return names[0] || "Conversation";
  };

  const getConvoInitial = (convo) => {
    const name = getConvoName(convo);
    return name.charAt(0).toUpperCase();
  };

  const filteredMembers = orgMembers.filter(m =>
    m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email.toLowerCase().includes(memberSearch.toLowerCase())
  ).filter(m => !selectedMembers.find(s => s.id === m.id));

  const filteredConvos = convoSearch.trim()
    ? conversations.filter(c => {
        const q = convoSearch.toLowerCase();
        const name = getConvoName(c).toLowerCase();
        const lastMsg = (c.last_message?.text || "").toLowerCase();
        return name.includes(q) || lastMsg.includes(q);
      })
    : conversations;

  return (
    // Bug 542 v3 (Eric 2026-07-24) — 3ème tentative. Le parent DashboardLayout
    // n'a que `min-h-screen` (pas de hauteur bornée), donc `h-full` ne peut
    // pas être calculé et le calc(100vh-180px) était encore trop grand
    // (dépassait la fenêtre visible, forçant le browser à scroller et
    // masquer les tabs Canaux/Conversations/Accès).
    //
    // Décomposition exacte du chrome au-dessus de nous :
    //   • Top navbar DashboardLayout : ~56px (pt-14)
    //   • Padding outer p-4 (top+bottom) : 32px
    //   • Tabs MessagingPage (py-3 + border) : ~50px
    //   • Bandeau simulation (si actif) : ~40px
    //   • Watermark version en pied : ~30px
    //   • Marge de sécurité : ~20px
    //   TOTAL ≈ 230px
    //
    // `100dvh` (dynamic viewport) est préféré à `100vh` sur mobile/browser
    // avec barre d'URL dynamique.
    // Vincent B548 v3 (Eric 2026-07-24) — Refonte de la hauteur.
    // Le parent (MessagingPage.js) fournit désormais une hauteur BORNÉE
    // (`h-[calc(100dvh-180px)] overflow-hidden`), donc on utilise simplement
    // `h-full` ici pour remplir l'espace disponible. Fini les calc() dupliqués
    // qui causaient des débordements aléatoires selon le zoom et le bandeau
    // simulation.
    <div className="h-full flex flex-col" data-testid="agora-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-teal-500/20 border border-teal-500/20 flex items-center justify-center">
          <ChatCircleDots size={22} className="text-teal-500" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold font-['Outfit']" data-testid="agora-title">{lang === "en" ? "Conversations" : "Conversations"}</h1>
          <p className="text-xs text-muted-foreground">{lang === "en" ? "Direct messages and groups" : "Messages directs et groupes"}</p>
        </div>
        <Button size="sm" onClick={() => { setShowNewConvo(true); loadOrgMembers(); }} className="gap-1.5 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="new-convo-btn">
          <Plus size={14} /> {lang === "en" ? "New" : "Nouveau"}
        </Button>
      </div>

      {/* Main area */}
      <div className="flex-1 flex border border-border rounded-xl overflow-hidden bg-card min-h-0">
        {/* Conversation list */}
        <div className={`w-full sm:w-80 border-r border-border flex flex-col ${mobileShowChat ? "hidden sm:flex" : "flex"}`}>
          <div className="p-3 border-b border-border space-y-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={convoSearch}
                onChange={(e) => setConvoSearch(e.target.value)}
                placeholder={lang === "en" ? "Filter conversations..." : "Filtrer mes conversations..."}
                className="pl-8 h-9 text-sm"
                data-testid="convo-search"
              />
            </div>
            {/* Vincent B597 (Eric 2026-07-28) — Toggle Actives / Archivées */}
            <div className="flex text-xs rounded-lg border border-border overflow-hidden" data-testid="agora-archive-toggle">
              <button
                type="button"
                onClick={() => setShowArchived(false)}
                className={`flex-1 py-1.5 font-semibold transition-colors ${!showArchived ? "bg-[#002FA7] text-white" : "bg-transparent text-muted-foreground hover:bg-secondary"}`}
                data-testid="agora-tab-active"
              >
                {lang === "en" ? "Active" : "Actives"}
              </button>
              <button
                type="button"
                onClick={() => setShowArchived(true)}
                className={`flex-1 py-1.5 font-semibold transition-colors inline-flex items-center justify-center gap-1 ${showArchived ? "bg-[#002FA7] text-white" : "bg-transparent text-muted-foreground hover:bg-secondary"}`}
                data-testid="agora-tab-archived"
              >
                <Archive size={12} />
                {lang === "en" ? "Archived" : "Archivées"}
              </button>
            </div>
            {/* Vincent B598.c (Eric 2026-07-29) — Hint 1× par session */}
            {showArchiveHint && !showArchived && (
              <p
                className="text-[10px] text-muted-foreground/80 leading-tight animate-pulse cursor-pointer"
                onClick={dismissArchiveHint}
                data-testid="agora-archive-hint"
              >
                {lang === "en"
                  ? "Tip: hover over a conversation to archive it"
                  : "Astuce : survole une conversation pour l'archiver"}
              </p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-10"><SpinnerGap size={24} className="animate-spin text-muted-foreground" /></div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-10 px-4 text-muted-foreground">
                <ChatCircleDots size={36} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">{lang === "en" ? "No conversations yet" : "Aucune conversation"}</p>
                <p className="text-xs mt-1">{lang === "en" ? "Start a new conversation" : "Commencez une nouvelle conversation"}</p>
              </div>
            ) : filteredConvos.length === 0 ? (
              <div className="text-center py-10 px-4 text-muted-foreground">
                <MagnifyingGlass size={28} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">{lang === "en" ? "No match" : "Aucun résultat"}</p>
                <p className="text-xs mt-1">«&nbsp;{convoSearch}&nbsp;»</p>
              </div>
            ) : filteredConvos.map(convo => (
              <div
                key={convo.id}
                role="button"
                tabIndex={0}
                onClick={() => openConvo(convo)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openConvo(convo); }}
                className={`w-full flex items-center gap-3 p-3 text-left hover:bg-secondary/50 transition-colors border-b border-border/50 cursor-pointer group ${activeConvo?.id === convo.id ? "bg-secondary" : ""}`}
                data-testid={`convo-${convo.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${convo.is_group ? "bg-teal-500/15" : "bg-[#002FA7]/10"}`}>
                  {convo.is_group ? <UsersThree size={18} className="text-teal-500" /> : <span className="text-sm font-bold text-[#002FA7]">{getConvoInitial(convo)}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm truncate">{getConvoName(convo)}</p>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">{formatTime(convo.last_message?.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {convo.last_message?.has_file ? "📎 " : ""}
                    {convo.last_message ? `${convo.last_message.sender_name?.split(" ")[0]}: ${convo.last_message.text?.substring(0, 40)}` : (lang === "en" ? "New conversation" : "Nouvelle conversation")}
                  </p>
                </div>
                {/* Vincent B597 (Eric 2026-07-28) — Bouton Archiver / Désarchiver */}
                <button
                  type="button"
                  onClick={(e) => toggleArchive(convo.id, !convo.is_archived, e)}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1.5 rounded hover:bg-[#002FA7]/10 text-muted-foreground hover:text-[#002FA7] transition-opacity flex-shrink-0"
                  title={convo.is_archived ? (lang === "en" ? "Restore" : "Désarchiver") : (lang === "en" ? "Archive" : "Archiver")}
                  aria-label={convo.is_archived ? (lang === "en" ? "Restore conversation" : "Désarchiver la conversation") : (lang === "en" ? "Archive conversation" : "Archiver la conversation")}
                  data-testid={`agora-archive-btn-${convo.id}`}
                >
                  {convo.is_archived ? <ArrowCounterClockwise size={14} /> : <Archive size={14} />}
                </button>
                {convo.unread > 0 && (
                  <span className="w-5 h-5 bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0 ml-1">{convo.unread}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className={`flex-1 flex flex-col ${!mobileShowChat ? "hidden sm:flex" : "flex"}`}>
          {activeConvo ? (
            <>
              {/* Chat header */}
              <div className="h-14 flex items-center gap-3 px-4 border-b border-border flex-shrink-0">
                <button onClick={() => setMobileShowChat(false)} className="sm:hidden p-1"><ArrowLeft size={20} /></button>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${activeConvo.is_group ? "bg-teal-500/15" : "bg-[#002FA7]/10"}`}>
                  {activeConvo.is_group ? <UsersThree size={16} className="text-teal-500" /> : <span className="text-sm font-bold text-[#002FA7]">{getConvoInitial(activeConvo)}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{getConvoName(activeConvo)}</p>
                  {activeConvo.is_group && <p className="text-[10px] text-muted-foreground truncate">{
                    // Bug 543 v2 (Eric 2026-07-24) — Trie les noms des
                    // participants par ordre alpha (prénom) au lieu de
                    // l'ordre arbitraire d'insertion en DB.
                    Object.values(activeConvo.member_names || {})
                      .slice()
                      .sort((a, b) => (a || "").localeCompare(b || "", "fr", { sensitivity: "base" }))
                      .join(", ")
                  }</p>}
                </div>
                {/* Vincent B591 (Eric 2026-07-28 SPEC STRICK) — Bouton éditer
                    les membres du groupe. Visible uniquement pour le créateur
                    ou pour les rôles admin/founder. */}
                {canEditMembers && (
                  <button
                    type="button"
                    onClick={openEditMembers}
                    className="flex-shrink-0 p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-[#002FA7] transition-colors"
                    data-testid="edit-group-members-btn"
                    title="Éditer les membres du groupe"
                    aria-label="Éditer les membres du groupe"
                  >
                    <PencilSimple size={16} />
                  </button>
                )}
                {/* Eric 2026-07-30 (B596 enhancement) — Toggle son « ding »
                    discret sur nouveau message. Muet par défaut. Persistant
                    dans localStorage (`knoria_msg_sound_enabled`). Sample-play
                    au clic quand on active, pour que l'utilisateur entende
                    ce qu'il choisit. */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !soundOn;
                    setSoundOn(next);
                    setSoundEnabled(next);
                    if (next) playDing(); // preview du son quand on l'active
                  }}
                  className={`flex-shrink-0 p-2 rounded-md hover:bg-muted transition-colors ${soundOn ? "text-[#002FA7]" : "text-muted-foreground"}`}
                  data-testid="agora-sound-toggle"
                  title={soundOn ? (lang === "fr" ? "Son activé — Cliquez pour muter" : "Sound on — Click to mute") : (lang === "fr" ? "Son muet — Cliquez pour activer" : "Muted — Click to enable sound")}
                  aria-label={soundOn ? "Son activé" : "Son muet"}
                  aria-pressed={soundOn}
                >
                  {soundOn ? <SpeakerHigh size={16} /> : <SpeakerSlash size={16} />}
                </button>
              </div>

              {/* Tâche 1425 — Hors de la zone défilante, pour la même
                  raison que dans les canaux. */}
              <PinnedPoll
                conversationId={activeConvo.id}
                currentUserId={user?.id}
                canAdmin={canEditContent(user)}
                cle={messages.length}
              />
              {/* Messages */}
              <div className="flex-1 relative min-h-0">
                <div
                  ref={messagesContainerRef}
                  onScroll={onMessagesScroll}
                  className="absolute inset-0 overflow-y-auto p-4 space-y-3"
                  data-testid="agora-messages-scroll"
                >
                {messages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`group flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
                      <div className={`${msg.kind === "poll" ? "max-w-[92%] w-full" : "max-w-[75%]"} ${isMine ? "order-2" : ""}`}>
                        {!isMine && activeConvo.is_group && <p className="text-[10px] text-muted-foreground mb-0.5 ml-1">{msg.sender_name?.split(" ")[0]}</p>}
                        {/* Tâche 1424 — Le sondage remplace la bulle : sa
                            question y est déjà, la redire au-dessus donnerait
                            à lire deux fois la même phrase. */}
                        {msg.kind === "poll" ? (
                          <PollCard
                            message={msg}
                            currentUserId={user?.id}
                            canAdmin={canEditContent(user)}
                            onChanged={(maj) => setMessages((cur) => cur.map((x) => (x.id === maj.id ? { ...x, ...maj } : x)))}
                          />
                        ) : (
                        <div className={`rounded-2xl px-3.5 py-2 ${isMine ? "bg-[#002FA7] text-white rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                          {msg.has_file && (
                            <a
                              href={`${API}/api/agora/messages/${msg.id}/file`}
                              target="_blank"
                              rel="noreferrer"
                              className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${isMine ? "bg-white/10 hover:bg-white/20" : "bg-background hover:bg-background/80"} transition-colors`}
                              data-testid={`file-${msg.id}`}
                            >
                              {msg.is_image ? <ImageIcon size={16} /> : <Paperclip size={16} />}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{msg.file_name}</p>
                                <p className="text-[10px] opacity-70">{formatFileSize(msg.file_size)}</p>
                              </div>
                              <DownloadSimple size={14} />
                            </a>
                          )}
                          {(!msg.has_file || msg.text !== msg.file_name) && <p className="text-sm whitespace-pre-wrap break-words">{texteMessage(msg, lang)}</p>}
                        </div>
                        )}
                        <div className={`flex items-center gap-1 mt-0.5 ${isMine ? "justify-end" : ""}`}>
                          <span className="text-[9px] text-muted-foreground">{formatTime(msg.created_at)}</span>
                          {isMine && <Checks size={10} className="text-teal-400" />}
                          {/* Vincent (Eric 2026-05-18, round 2) — Bouton Supprimer
                              TOUJOURS visible. Auparavant `opacity-0 group-hover:opacity-100`
                              rendait le bouton invisible sans hover → l'utilisateur ne
                              savait pas qu'il pouvait supprimer ses messages. */}
                          {isMine && (
                            <button
                              type="button"
                              onClick={() => deleteDmMessage(msg)}
                              className="inline-flex items-center gap-1 text-rose-600 hover:text-white hover:bg-rose-600 px-1.5 py-0.5 rounded transition-colors text-[10px] font-semibold border border-rose-300 hover:border-rose-600 ml-1 bg-white"
                              title={lang === "fr" ? "Supprimer définitivement ce message pour tous les participants" : "Permanently delete this message for all participants"}
                              data-testid={`dm-msg-delete-${msg.id}`}
                            >
                              <Trash size={10} weight="bold" />
                              <span>{lang === "fr" ? "Supprimer" : "Delete"}</span>
                            </button>
                          )}
                        </div>
                        {/* Vincent #115 (2026-05-12) → #120 (2026-05-13) — Per-DM-message
                            indexation controls. Visible UNIQUEMENT pour les conversations
                            DM individuelles (pas les groupes) ET aux rôles expérimentés :
                            Fondateur, Admin, Concepteur, Directeur/agent expérimenté.
                            Conseillers et Stagiaires : pas d'accès. */}
                        {!activeConvo.is_group && canIndexDm && (
                          <div className={`flex items-center gap-1 mt-1 ${isMine ? "justify-end" : ""}`} data-testid={`dm-index-controls-${msg.id}`}>
                            <span className="text-[9px] text-muted-foreground inline-flex items-center gap-1">
                              <Robot size={9} weight="bold" />
                              {lang === "fr" ? "Chatbot :" : "Chatbot:"}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleDmIndex(msg, true)}
                              className={`px-1.5 rounded text-[9px] ${msg.index_override === true ? "bg-teal-100 text-teal-800 font-bold" : "hover:bg-zinc-100 text-zinc-500"}`}
                              title={lang === "fr" ? "Partager avec le chatbot" : "Share with chatbot"}
                              data-testid={`dm-index-include-${msg.id}`}
                            >
                              <CheckCircle size={10} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleDmIndex(msg, false)}
                              className={`px-1.5 rounded text-[9px] ${msg.index_override === false ? "bg-red-100 text-red-800 font-bold" : "hover:bg-zinc-100 text-zinc-500"}`}
                              title={lang === "fr" ? "Exclure du chatbot" : "Exclude from chatbot"}
                              data-testid={`dm-index-exclude-${msg.id}`}
                            >
                              <X size={10} weight="bold" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleDmIndex(msg, null)}
                              className={`px-1.5 rounded text-[9px] ${msg.index_override == null ? "bg-zinc-100 text-zinc-700 font-bold" : "hover:bg-zinc-100 text-zinc-500"}`}
                              title={lang === "fr" ? "Privé (défaut)" : "Private (default)"}
                              data-testid={`dm-index-default-${msg.id}`}
                            >
                              =
                            </button>
                            <span className="text-[9px] italic text-muted-foreground ml-0.5" data-testid={`dm-index-status-${msg.id}`}>
                              {msg.indexed_by_chatbot ? (lang === "fr" ? "indexé" : "indexed") : (lang === "fr" ? "privé" : "private")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
                </div>
                {/* Vincent B596.b (Eric 2026-07-28) — Bouton flottant « ↓ Nouveaux messages » */}
                {newMsgCount > 0 && (
                  <button
                    type="button"
                    onClick={scrollToLatest}
                    className={`absolute bottom-3 right-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#002FA7] hover:bg-[#002280] text-white text-xs font-semibold shadow-lg transition-colors ${pulse ? "animate-bounce" : ""}`}
                    data-testid="agora-new-msgs-btn"
                    aria-label={lang === "fr" ? `${newMsgCount} nouveau(x) message(s)` : `${newMsgCount} new message(s)`}
                  >
                    <span>↓</span>
                    <span data-testid="agora-new-msgs-count">{newMsgCount}</span>
                    <span>{lang === "fr" ? (newMsgCount > 1 ? "nouveaux messages" : "nouveau message") : (newMsgCount > 1 ? "new messages" : "new message")}</span>
                  </button>
                )}
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-3 border-t border-border flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 hover:bg-secondary rounded-lg transition-colors" data-testid="attach-btn">
                  {uploading ? <SpinnerGap size={20} className="animate-spin text-muted-foreground" /> : <Paperclip size={20} className="text-muted-foreground" />}
                </button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={e => { handleFileUpload(e.target.files?.[0]); e.target.value = ""; }} />
                {canCreatePoll && (
                  <button
                    type="button"
                    onClick={() => setShowPollComposer(true)}
                    className="p-2 hover:bg-secondary rounded-lg transition-colors text-indigo-700"
                    data-testid="agora-poll-btn"
                    title={lang === "en" ? "Create a poll in this conversation" : "Créer un sondage dans cette conversation"}
                  >
                    <ChartBar size={20} weight="bold" />
                  </button>
                )}
                <Input
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  placeholder={lang === "en" ? "Type a message..." : "Ecrivez un message..."}
                  className="flex-1 h-10"
                  data-testid="message-input"
                />
                <Button type="submit" size="sm" disabled={sending || !newMsg.trim()} className="h-10 w-10 p-0 bg-[#002FA7] hover:bg-[#002280] text-white rounded-lg" data-testid="send-btn">
                  {sending ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneRight size={16} />}
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <ChatCircleDots size={48} className="mx-auto mb-3 opacity-15" />
                <p className="font-medium text-sm">{lang === "en" ? "Select a conversation" : "Sélectionnez une conversation"}</p>
                <p className="text-xs mt-1">{lang === "en" ? "or start a new one" : "ou demarrez-en une nouvelle"}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New conversation modal */}
      {showNewConvo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowNewConvo(false)}>
          <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="new-convo-modal">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold">{lang === "en" ? "New conversation" : "Nouvelle conversation"}</h3>
              <button onClick={() => setShowNewConvo(false)} className="p-1 hover:bg-secondary rounded"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {selectedMembers.length > 1 && (
                <>
                  <Input placeholder={lang === "en" ? "Group name (optional)" : "Nom du groupe (optionnel)"} value={groupName} onChange={e => setGroupName(e.target.value)} className="h-9" data-testid="group-name-input" />
                  {/* Bug 543 v2 bonus (Eric 2026-07-24) — Preview live du nom
                      qui sera stocké en DB. Se met à jour à chaque coche/
                      décoche de membre. Élimine toute confusion sur l'ordre
                      final affiché dans la liste des conversations. */}
                  {(() => {
                    const preview = [...selectedMembers]
                      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }))
                      .map(m => m.name.split(" ")[0])
                      .join(", ");
                    const isCustom = groupName.trim().length > 0;
                    return (
                      <div
                        className="flex items-start gap-1.5 px-2.5 py-1.5 rounded-md bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-600 dark:text-zinc-400"
                        data-testid="group-name-preview"
                      >
                        <Sparkle size={11} className="text-[#002FA7] flex-shrink-0 mt-0.5" weight="fill" />
                        <span>
                          <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                            {lang === "fr" ? "Aperçu du nom du groupe :" : "Group name preview:"}
                          </span>{" "}
                          <span className="font-medium text-zinc-900 dark:text-zinc-100">
                            {isCustom ? groupName.trim() : preview}
                          </span>
                          {isCustom && (
                            <span className="ml-1 text-zinc-500">
                              {lang === "fr" ? "(personnalisé)" : "(custom)"}
                            </span>
                          )}
                          {!isCustom && (
                            <span className="ml-1 text-zinc-500">
                              {lang === "fr" ? "(auto — trié par prénom)" : "(auto — sorted by first name)"}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                </>
              )}
              {selectedMembers.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground" data-testid="selected-members-count">
                      {selectedMembers.length} {lang === "en" ? (selectedMembers.length > 1 ? "members" : "member") : (selectedMembers.length > 1 ? "membres" : "membre")}
                    </span>
                    {selectedMembers.length > 1 && (
                      <button
                        onClick={() => setSelectedMembers([])}
                        className="text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
                        data-testid="clear-selected-members"
                      >
                        {lang === "en" ? "Clear all" : "Tout effacer"}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedMembers.map(m => (
                      <span key={m.id} className="flex items-center gap-1 px-2 py-1 bg-[#002FA7]/10 text-[#002FA7] rounded-full text-xs font-medium">
                        {m.name.split(" ")[0]}
                        <button onClick={() => setSelectedMembers(s => s.filter(x => x.id !== m.id))}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Input placeholder={lang === "en" ? "Search members..." : "Rechercher un membre..."} value={memberSearch} onChange={e => setMemberSearch(e.target.value)} className="h-9" data-testid="member-search" />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredMembers.map(m => (
                  <button key={m.id} onClick={() => setSelectedMembers(s => [...s, m])} className="w-full flex items-center gap-3 p-2 hover:bg-secondary rounded-lg text-left transition-colors" data-testid={`member-${m.id}`}>
                    <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center"><span className="text-xs font-bold text-[#002FA7]">{m.name.charAt(0)}</span></div>
                    <div><p className="text-sm font-medium">{m.name}</p><p className="text-[10px] text-muted-foreground">{m.display_role || m.email}</p></div>
                  </button>
                ))}
                {filteredMembers.length === 0 && orgMembers.length > 0 && <p className="text-xs text-center text-muted-foreground py-4">{lang === "en" ? "No members found" : "Aucun membre trouvé"}</p>}
                {orgMembers.length === 0 && <p className="text-xs text-center text-muted-foreground py-4">{lang === "en" ? "No other members in your organization" : "Aucun autre membre dans votre organisation"}</p>}
              </div>
            </div>
            <div className="p-4 border-t border-border">
              <Button onClick={startNewConvo} disabled={selectedMembers.length === 0} className="w-full bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="create-convo-btn">
                {lang === "en" ? "Start conversation" : "Démarrer la conversation"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vincent B591 (Eric 2026-07-28 SPEC STRICK) — Modal d'édition des
          membres d'un groupe. Créateur du groupe OU admin/founder. */}
      {showEditMembers && activeConvo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditMembers(false)}>
          <div className="bg-background rounded-2xl w-full max-w-md flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()} data-testid="edit-members-modal">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base">Éditer les membres</h3>
                <p className="text-[11px] text-muted-foreground">{getConvoName(activeConvo)}</p>
              </div>
              <button onClick={() => setShowEditMembers(false)} data-testid="close-edit-members-btn"><X size={20} /></button>
            </div>
            <div className="p-3 border-b border-border">
              <Input
                placeholder="Rechercher un membre…"
                value={editMembersSearch}
                onChange={(e) => setEditMembersSearch(e.target.value)}
                className="h-9"
                data-testid="edit-members-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {orgMembers
                .filter((m) => {
                  const q = editMembersSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    (m.name || "").toLowerCase().includes(q) ||
                    (m.email || "").toLowerCase().includes(q) ||
                    (m.display_role || "").toLowerCase().includes(q)
                  );
                })
                .map((m) => {
                const isMember = editMembersSelected.has(m.id);
                const isCreator = activeConvo.created_by === m.id;
                return (
                  <label key={m.id} className={`w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer ${isMember ? "bg-[#002FA7]/5" : "hover:bg-secondary"}`} data-testid={`edit-member-row-${m.id}`}>
                    <input
                      type="checkbox"
                      checked={isMember}
                      disabled={isCreator}
                      onChange={(e) => {
                        setEditMembersSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(m.id); else next.delete(m.id);
                          return next;
                        });
                      }}
                      data-testid={`edit-member-checkbox-${m.id}`}
                      className="h-4 w-4"
                    />
                    <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-[#002FA7]">{m.name.charAt(0)}</span></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{m.name}{isCreator && <span className="text-[10px] text-muted-foreground ml-1">(créateur)</span>}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{m.display_role || m.email}</p>
                    </div>
                  </label>
                );
              })}
              {orgMembers.length === 0 && <p className="text-xs text-center text-muted-foreground py-6">Aucun autre membre dans l&apos;organisation</p>}
            </div>
            <div className="p-4 border-t border-border flex gap-2">
              <Button variant="outline" onClick={() => setShowEditMembers(false)} className="flex-1" data-testid="cancel-edit-members-btn">Annuler</Button>
              <Button onClick={saveMembers} disabled={editMembersBusy} className="flex-1 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-edit-members-btn">
                {editMembersBusy ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tâche 1424 — Fenêtre de création d'un sondage ── */}
      {showPollComposer && activeConvo && (
        <PollComposer
          conversationId={activeConvo.id}
          onClose={() => setShowPollComposer(false)}
          onCreated={(sondage) => setMessages((cur) => [...cur, sondage])}
        />
      )}

    </div>
  );
}
